import React, { useMemo } from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ProviderCard, PaginationLinks } from '../../../components';
import { ensureListing, ensureUser } from '../../../util/data';

import css from './SearchResultsPanel.module.css';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  // Group listings by provider (author)
  const providersWithListings = useMemo(() => {
    const providerMap = new Map();

    listings.forEach(listing => {
      const listingObj = ensureListing(listing);
      const author = listingObj.author;
      
      if (!author) {
        return;
      }

      const authorObj = ensureUser(author);
      const providerId = authorObj.id?.uuid;

      if (!providerId) {
        return;
      }

      if (!providerMap.has(providerId)) {
        providerMap.set(providerId, {
          provider: authorObj,
          listings: [],
        });
      }

      providerMap.get(providerId).listings.push(listingObj);
    });

    // Convert map to array
    return Array.from(providerMap.values());
  }, [listings]);

  return (
    <div className={classes}>
      <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {providersWithListings.map(({ provider, listings: providerListings }, index) => {
          const providerId = provider.id?.uuid;
          return (
            <ProviderCard
              className={css.listingCard}
              key={providerId}
              provider={provider}
              listings={providerListings}
              renderSizes={cardRenderSizes(isMapVariant)}
              deferFetchMs={index * 150}
            />
          );
        })}
        {props.children}
      </div>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
